import React, { useMemo, useState, useEffect, useRef } from 'react';
import { convertSecondsToTimeString } from '@goldcast/player-config/src/utils/timeUtils';
import { ElementResizeSideEnum } from '../TimelineTrackElementLanes/constants';
import type { ElementResizeSide } from '../TimelineTrackElementLanes/types';

interface TimelineTimeTooltipProps {
  timeSide: ElementResizeSide;
  startTime: number;
  endTime: number;
  elementRef: React.RefObject<HTMLDivElement>;
  isVisible: boolean;
}

export function TimelineTimeTooltip({ timeSide, startTime, endTime, elementRef, isVisible }: TimelineTimeTooltipProps) {
  const time = useMemo(() => {
    if (timeSide === ElementResizeSideEnum.CENTER) {
      return { from: startTime, to: endTime };
    }
    return {
      [ElementResizeSideEnum.START]: startTime,
      [ElementResizeSideEnum.END]: endTime
    }[timeSide];
  }, [startTime, endTime, timeSide]);

  const timeDisplay = useMemo(() => {
    if (timeSide === ElementResizeSideEnum.CENTER && typeof time !== 'number') {
      return `${convertSecondsToTimeString(time.from, 'mm:ss:S')} - ${convertSecondsToTimeString(time.to, 'mm:ss:S')}`;
    }
    return convertSecondsToTimeString(time as number, 'mm:ss:S');
  }, [time, timeSide]);

  const [tooltipPosition, setTooltipPosition] = useState({
    left: 0,
    bottom: 0,
    transform: 'translateX(-50%)'
  });
  const animationFrameRef = useRef<number>();

  const updateTooltipPosition = useMemo(() => {
    return () => {
      if (!elementRef.current) {
        return;
      }
      const rect = elementRef.current.getBoundingClientRect();
      const elementWidth = rect.width;
      let transform = 'translateX(-50%)';
      if (timeSide === ElementResizeSideEnum.END) {
        transform = `translateX(calc(-50% + ${elementWidth / 2}px))`;
      } else if (timeSide === ElementResizeSideEnum.START) {
        transform = `translateX(calc(-50% - ${elementWidth / 2}px))`;
      }
      setTooltipPosition({
        left: rect.left + rect.width / 2,
        bottom: window.innerHeight - rect.top + 4,
        transform
      });
    };
  }, [timeSide, elementRef]);

  // Update position when visible state changes or timeSide changes
  useEffect(() => {
    if (isVisible) {
      updateTooltipPosition();
    }
  }, [isVisible, updateTooltipPosition]);

  // Continuously update position during drag operations
  useEffect(() => {
    if (!isVisible) {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      return;
    }

    const animate = () => {
      updateTooltipPosition();
      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isVisible, updateTooltipPosition]);

  if (!isVisible) {
    return null;
  }

  return (
    <div
      className="fixed z-50 whitespace-nowrap rounded-sm border border-zinc-900 bg-zinc-900 px-1.5 py-0.5 text-2xs leading-[15px] text-white"
      style={{
        left: `${tooltipPosition.left}px`,
        bottom: `${tooltipPosition.bottom}px`,
        transform: tooltipPosition.transform
      }}
    >
      {timeDisplay}
    </div>
  );
}
